import React, { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
	Head,
	Wrapper,
	CancelOrderForm,
	CancelOrderButton,
	ContentWrapper,
	ContentLoader,
	renderErrorMessage,
	withLocalization,
} from '@adac-mp/components';
import {
	OrderService,
	OfferContext,
	IOrderResponse,
	VignetteContext,
	getMangeOrderIntroCollection,
	setRouteLangParam,
	Locales,
	ITypeVignettesOrderManagementFields,
	getUrlBasePath,
	TollService,
} from '@adac-mp/shared';
import { redirectToErrorPage } from '@adac-mp/shared/src/services/axios-config';
import { useViewport } from '@adac-mp/components/src/hooks/useViewport';
import { trackFunnelView } from '@adac-mp/components/src/utils/tracking/tracking';
import { useQuery } from '@apollo/client';

export const CancelOrder = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const { lang } = useParams();
	const { allCountries, cancelFormData, updateCancelFormData, updateAllCountries } = useContext(OfferContext);
	const { contentfulLabels, updateContentfulLabels } = useContext(VignetteContext);
	const [isLoading, setIsLoading] = useState(false);
	const [validateFields, setValidateFields] = useState(false);
	const [hasError, setHasError] = useState<boolean>(false);
	const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined);
	const { viewport } = useViewport();
	const { error, data, loading } = useQuery(getMangeOrderIntroCollection, {
		variables: { lang: lang || 'de' },
	});

	useEffect(() => {
		const fetchAllCountries = async () =>
			await TollService.getCountries(lang).then((resp) => {
				updateAllCountries(resp.countries);
			});

		if (!allCountries) {
			fetchAllCountries();
		}
	}, []);

	useEffect(() => {
		if (!error) {
			updateContentfulLabels(data?.vignettesOrderManagementCollection.items[0]);
		}
	}, [data]);

	const currentLabels = contentfulLabels as ITypeVignettesOrderManagementFields;

	useEffect(() => {
		const type = location.pathname.includes('vignette') ? 'vignette' : 'dkv';
		if (type) {
			trackFunnelView({
				label: 'Übersicht',
				element: 'Bestellung verwalten',
				trackOnce: true,
			});
		}
	}, []);

	const onSuccess = () => {
		navigate(getUrlBasePath(`/cancel-order-request${setRouteLangParam(lang)}`));
	};

	const handleResponse = (res: IOrderResponse) => {
		setIsLoading(false);
		if (res.code === 200) {
			onSuccess();
		} else if (res.code === 500) {
			if (res.errors[0].errorNumber === 10010) {
				setErrorMsg(
					currentLabels?.errorMessageIntro ||
						'Unter den angegebenen Daten konnten wir leider keine Bestellung finden. Bitte überprüfen Sie Ihre Angaben und versuchen Sie es erneut.',
				);
			} else {
				setErrorMsg(res.errors[0].customerMessage);
			}
		} else if (res.redirectToErrorPage) {
			redirectToErrorPage(res.errors);
		}
	};

	const handleClick = () => {
		if (!validateFields) {
			setValidateFields(true);
		}
		if (hasError || !cancelFormData) {
			return;
		}
		sessionStorage.setItem('cancelationDetails', JSON.stringify(cancelFormData));
		setIsLoading(true);
		OrderService.createEmailUuid(cancelFormData, lang as Locales).then((res) => handleResponse(res));
	};

	return (
		<>
			{loading ? (
				<ContentLoader isLoading hasPortal={false} />
			) : (
				<>
					<Wrapper>
						<ContentLoader isLoading={isLoading} />
						<Head
							headlineSize="h2"
							headline={currentLabels?.headlineIntro || 'Bestellung verwalten'}
							description={
								currentLabels?.textIntro ||
								'Bearbeiten oder stornieren Sie die Bestellung Ihrer Digitalen Vignette oder Streckenmaut.'
							}
						/>
						<ContentWrapper>
							{errorMsg && renderErrorMessage({ message: errorMsg, className: 'mb-5' })}
							{allCountries && (
								<CancelOrderForm
									countries={allCountries}
									validateFields={validateFields}
									setHasError={setHasError}
									updateCancelForm={updateCancelFormData}
								/>
							)}
						</ContentWrapper>
						{viewport === 'desktop' && <CancelOrderButton onClick={handleClick} />}
					</Wrapper>
					{viewport === 'mobile' && <CancelOrderButton onClick={handleClick} />}
				</>
			)}
		</>
	);
};

export const CancelOrderLocalization = withLocalization(CancelOrder);
